import type { SupportedLanguage } from '@/components/multilingual/LanguageSelector';

/** Date of the last self-assessment. Update whenever the audit is repeated. */
export const ACCESSIBILITY_ASSESSED_ON = '2026-08-15';

interface AccessibilitySection {
  title: string;
  body: string[];
}

interface AccessibilityCopy {
  breadcrumb: string;
  title: string;
  assessedOn: string;
  sections: AccessibilitySection[];
}

export const accessibilityCopy: Record<SupportedLanguage, AccessibilityCopy> = {
  de: {
    breadcrumb: 'Barrierefreiheit',
    title: 'Erklärung zur Barrierefreiheit',
    assessedOn: 'Stand der Selbstbewertung',
    sections: [
      {
        title: 'Geltungsbereich',
        body: [
          'Diese Erklärung gilt für die Website und Webanwendung unter paperverse.io einschließlich Registrierung, Upload, Explore, KI-Reviews, PaperVerse Writer sowie des Bestell- und Zahlungsprozesses.',
          'Anbieter ist die PaperVerse UG (haftungsbeschränkt).',
        ],
      },
      {
        title: 'Angestrebter Standard',
        body: [
          'Wir orientieren uns an den Anforderungen des Barrierefreiheitsstärkungsgesetzes (BFSG) und der Barrierefreiheitsstärkungsgesetz-Verordnung (BFSGV) in Verbindung mit EN 301 549 und WCAG 2.1 Stufe AA.',
        ],
      },
      {
        title: 'Stand der Vereinbarkeit',
        body: [
          'Dieses Angebot ist mit den genannten Anforderungen teilweise vereinbar. Die Bewertung beruht auf einer Selbstbewertung, nicht auf einer Prüfung durch Dritte.',
          'Bekannte Einschränkungen: Der PaperVerse Writer (LaTeX-Editor, Chat und PDF-Vorschau) ist derzeit nur eingeschränkt mit Screenreadern nutzbar; die PDF-Vorschau wird als Grafik gerendert, der zugehörige Text steht jedoch im Editor und als Download zur Verfügung. Einzelne Diagramme in Statistik- und Admin-Ansichten verfügen noch nicht über vollständige Textalternativen. Nicht alle interaktiven Elemente erfüllen bereits durchgängig die Kontrast- und Fokusanforderungen.',
          'Vorrangig bearbeitet werden derzeit Registrierung, Anmeldung, Upload sowie der Bestell- und Kündigungsprozess.',
        ],
      },
      {
        title: 'Ausnahmen',
        body: [
          'Von Nutzerinnen und Nutzern hochgeladene Inhalte (insbesondere PDF-Dateien wissenschaftlicher Arbeiten und externe Preprints) stammen nicht von uns und können nicht barrierefrei gestaltet werden. Wir stellen für diese Inhalte, soweit verfügbar, extrahierte Textfassungen, Abstracts und strukturierte Zusammenfassungen bereit.',
          'Ob und in welchem Umfang die Ausnahme für Kleinstunternehmen nach § 3 Abs. 3 BFSG greift, wird laufend geprüft; wir gehen unabhängig davon die genannten Punkte an.',
        ],
      },
      {
        title: 'Barrieren melden — Feedback',
        body: [
          'Sie können uns Barrieren melden und barrierefrei aufbereitete Informationen anfordern: contact@paperverse.io. Bitte beschreiben Sie die betroffene Seite und die Art der Barriere. Wir antworten in der Regel innerhalb von 14 Tagen.',
        ],
      },
      {
        title: 'Durchsetzungsverfahren',
        body: [
          'Wenn Sie mit unserer Antwort nicht zufrieden sind, können Sie sich an die zuständige Marktüberwachungsbehörde für Barrierefreiheit (Marktüberwachungsstelle der Länder für die Barrierefreiheit von Produkten und Dienstleistungen, MLBF, Magdeburg) wenden.',
        ],
      },
    ],
  },
  en: {
    breadcrumb: 'Accessibility',
    title: 'Accessibility statement',
    assessedOn: 'Date of self-assessment',
    sections: [
      {
        title: 'Scope',
        body: [
          'This statement applies to the website and web application at paperverse.io, including sign-up, upload, Explore, AI reviews, PaperVerse Writer, and the purchase and cancellation flows.',
          'The provider is PaperVerse UG (haftungsbeschränkt).',
        ],
      },
      {
        title: 'Standard we aim for',
        body: [
          'We follow the German Accessibility Strengthening Act (BFSG) and its ordinance (BFSGV) in conjunction with EN 301 549 and WCAG 2.1 level AA.',
        ],
      },
      {
        title: 'Conformance status',
        body: [
          'This service is partially conformant with the requirements above. The assessment is a self-assessment and has not been verified by a third party.',
          'Known limitations: PaperVerse Writer (LaTeX editor, chat and PDF preview) currently has limited screen-reader support; the PDF preview renders as a graphic, but the underlying text is available in the editor and as a download. Some charts in statistics and admin views do not yet have full text alternatives. Not all interactive elements consistently meet contrast and focus requirements.',
          'Sign-up, login, upload, and the purchase and cancellation flows are being addressed first.',
        ],
      },
      {
        title: 'Exemptions',
        body: [
          'Content uploaded by users — in particular PDF files of scientific papers and external preprints — does not originate from us and cannot be made accessible by us. Where available we provide extracted text, abstracts and structured summaries for such content.',
          'Whether the micro-enterprise exemption under § 3(3) BFSG applies is being assessed on an ongoing basis; we are addressing the points above regardless.',
        ],
      },
      {
        title: 'Feedback — reporting barriers',
        body: [
          'You can report accessibility barriers and request information in an accessible format at contact@paperverse.io. Please describe the page concerned and the barrier you encountered. We usually reply within 14 days.',
        ],
      },
      {
        title: 'Enforcement procedure',
        body: [
          'If you are not satisfied with our response, you can contact the competent German market surveillance authority for accessibility (Marktüberwachungsstelle der Länder für die Barrierefreiheit von Produkten und Dienstleistungen, MLBF, Magdeburg).',
        ],
      },
    ],
  },
  fr: {
    breadcrumb: 'Accessibilité',
    title: 'Déclaration d’accessibilité',
    assessedOn: 'Date de l’auto-évaluation',
    sections: [
      {
        title: 'Champ d’application',
        body: [
          'La présente déclaration s’applique au site et à l’application web paperverse.io, y compris l’inscription, le dépôt, Explore, les évaluations IA, PaperVerse Writer ainsi que les parcours d’achat et de résiliation.',
          'Le fournisseur est PaperVerse UG (haftungsbeschränkt).',
        ],
      },
      {
        title: 'Norme visée',
        body: [
          'Nous nous appuyons sur la loi allemande sur le renforcement de l’accessibilité (BFSG) et son règlement (BFSGV), en lien avec la norme EN 301 549 et les WCAG 2.1 niveau AA.',
        ],
      },
      {
        title: 'État de conformité',
        body: [
          'Ce service est partiellement conforme aux exigences ci-dessus. L’évaluation est une auto-évaluation, non vérifiée par un tiers.',
          'Limites connues : PaperVerse Writer (éditeur LaTeX, chat et aperçu PDF) offre actuellement une prise en charge limitée des lecteurs d’écran ; l’aperçu PDF est rendu sous forme graphique, mais le texte reste disponible dans l’éditeur et en téléchargement. Certains graphiques des vues statistiques et d’administration n’ont pas encore d’alternative textuelle complète. Les exigences de contraste et de focus ne sont pas encore respectées partout.',
          'L’inscription, la connexion, le dépôt ainsi que les parcours d’achat et de résiliation sont traités en priorité.',
        ],
      },
      {
        title: 'Exceptions',
        body: [
          'Les contenus déposés par les utilisateurs — notamment les PDF d’articles scientifiques et les prépublications externes — ne proviennent pas de nous et ne peuvent pas être rendus accessibles par nos soins. Lorsque cela est possible, nous fournissons le texte extrait, des résumés et des synthèses structurées.',
          'L’applicabilité de l’exception pour microentreprises (§ 3, al. 3 BFSG) est évaluée en continu ; nous traitons les points ci-dessus indépendamment.',
        ],
      },
      {
        title: 'Signaler un obstacle',
        body: [
          'Vous pouvez signaler des obstacles d’accessibilité et demander des informations dans un format accessible à contact@paperverse.io. Merci de préciser la page concernée et la nature de l’obstacle. Nous répondons généralement sous 14 jours.',
        ],
      },
      {
        title: 'Voie de recours',
        body: [
          'Si notre réponse ne vous satisfait pas, vous pouvez saisir l’autorité allemande de surveillance du marché compétente en matière d’accessibilité (MLBF, Magdebourg).',
        ],
      },
    ],
  },
  es: {
    breadcrumb: 'Accesibilidad',
    title: 'Declaración de accesibilidad',
    assessedOn: 'Fecha de la autoevaluación',
    sections: [
      {
        title: 'Ámbito de aplicación',
        body: [
          'Esta declaración se aplica al sitio web y a la aplicación web paperverse.io, incluidos el registro, la subida, Explore, las revisiones con IA, PaperVerse Writer y los procesos de compra y cancelación.',
          'El proveedor es PaperVerse UG (haftungsbeschränkt).',
        ],
      },
      {
        title: 'Norma de referencia',
        body: [
          'Nos guiamos por la Ley alemana de refuerzo de la accesibilidad (BFSG) y su reglamento (BFSGV), en relación con la norma EN 301 549 y las WCAG 2.1 nivel AA.',
        ],
      },
      {
        title: 'Estado de conformidad',
        body: [
          'Este servicio es parcialmente conforme con los requisitos anteriores. La evaluación es una autoevaluación y no ha sido verificada por terceros.',
          'Limitaciones conocidas: PaperVerse Writer (editor LaTeX, chat y vista previa del PDF) tiene actualmente un soporte limitado para lectores de pantalla; la vista previa del PDF se representa como gráfico, aunque el texto está disponible en el editor y como descarga. Algunos gráficos de las vistas de estadísticas y administración aún no tienen alternativas textuales completas. No todos los elementos interactivos cumplen de forma uniforme los requisitos de contraste y foco.',
          'Se abordan primero el registro, el inicio de sesión, la subida y los procesos de compra y cancelación.',
        ],
      },
      {
        title: 'Excepciones',
        body: [
          'Los contenidos subidos por las personas usuarias —en particular archivos PDF de artículos científicos y prepublicaciones externas— no proceden de nosotros y no podemos hacerlos accesibles. Cuando es posible, ofrecemos el texto extraído, resúmenes y síntesis estructuradas.',
          'La aplicabilidad de la excepción para microempresas (§ 3, ap. 3 BFSG) se evalúa de forma continua; abordamos los puntos anteriores con independencia de ello.',
        ],
      },
      {
        title: 'Comunicar barreras',
        body: [
          'Puede comunicar barreras de accesibilidad y solicitar información en un formato accesible en contact@paperverse.io. Indique la página afectada y el tipo de barrera. Solemos responder en un plazo de 14 días.',
        ],
      },
      {
        title: 'Procedimiento de reclamación',
        body: [
          'Si no queda satisfecho con nuestra respuesta, puede dirigirse a la autoridad alemana de vigilancia del mercado competente en materia de accesibilidad (MLBF, Magdeburgo).',
        ],
      },
    ],
  },
};
